import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ExaminationHeader from "../../components/examinationHeader/examinationHeader";
import PracticeReadyScreen from "../../components/PracticeReadyScreen/practiceReadyScreen";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import styles from "../../publicAsset/userPracticeScreen.module.scss";
import ExaminationContent from "../../components/examinationContent/examinationContent";
import Section25ExaminationContent from "../../components/examinationContent/Section25ExaminationContent";
import NavigationConfirmModal from "../../components/navigationConfirmModal/navigationConfirmModal";
import { statusType } from "../../utils/Enum/statusType";
import { checkActiveRecord, clearRecordError, createRecordAndGetFirstQuestion, resumeRecordAndGetQuestion } from "../../reduxStore/actions/recordActionsHelper";
import useNavigationGuard from "../../utils/useNavigationGuard";
import { questionData$, questionLoading$, questionError$, selectedSection$ } from "../../reduxStore/selectors/questionSelectors";
import { recordData$, recordLoading$, recordError$ } from "../../reduxStore/selectors/recordSelectors";
import { usersData$ } from "../../reduxStore/selectors/userSelectors";
import ContinueSessionModal from "../../components/continueSessionModal/ContinueSessionModal";
import { sessionModeType } from "../../utils/Enum/sessionModeType";
import useDemoPracticeLimit, { DEMO_PRACTICE_LIMIT } from "../../utils/useDemoPracticeLimit";

const UserPracticeScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const questionData = useSelector(questionData$);
    const isLoading = useSelector(questionLoading$);
    const error = useSelector(questionError$);
    const selectedSection = useSelector(selectedSection$);
    const recordData = useSelector(recordData$);
    const recordLoading = useSelector(recordLoading$);
    const recordError = useSelector(recordError$);
    const userData = useSelector(usersData$);

    const [isExaminationStarted, setIsExaminationStarted] = useState(false);
    const [showContinueModal, setShowContinueModal] = useState(false);
    const [showDemoLimitModal, setShowDemoLimitModal] = useState(false);
    const [activeRecord, setActiveRecord] = useState(null);
    const [hasCheckedActiveRecord, setHasCheckedActiveRecord] = useState(false);
    const [isProcessingRecord, setIsProcessingRecord] = useState(false);
    const [practiceMode, setPracticeMode] = useState(sessionModeType.UNLIMITED);

    const { hasReachedLimit, consumeTrial } = useDemoPracticeLimit();

    // Navigation guard for active examination sessions
    const hasActiveSession = isExaminationStarted && (activeRecord?.status === statusType.ACTIVE || activeRecord?.status === statusType.PAUSED);

    const {
        showNavigationModal,
        handlePauseAndLeave,
        handleEndAndLeave,
        handleContinueSession,
        interceptNavigation
    } = useNavigationGuard(
        hasActiveSession,
        activeRecord?._id
    );

    const handlePracticeMenuClick = () => {
        if (hasActiveSession) {
            interceptNavigation(null);
        }
    };

    // Check for active records when component mounts or section changes
    useEffect(() => {
        if (selectedSection && userData?.id) {
            // Reset flag when checking for active records
            setIsProcessingRecord(true);
            setHasCheckedActiveRecord(false);
            dispatch(checkActiveRecord.request({
                userId: userData.id,
                section: selectedSection
            }));
        }
    }, [dispatch, selectedSection, userData?.id]);

    // Handle active record check response and record updates
    useEffect(() => {
        if (recordData) {
            // Handle checkActiveRecord response (has activeRecord property)
            if (recordData.activeRecord && !hasCheckedActiveRecord) {
                // Only set active record if it matches the currently selected section
                if (recordData.activeRecord.section === selectedSection) {
                    setActiveRecord(recordData.activeRecord);
                    setShowContinueModal(true);
                    setHasCheckedActiveRecord(true);
                    setIsProcessingRecord(false);
                }
            }
            // Handle createNewRecord response (new record created)
            else if (recordData._id && recordData.status === statusType.ACTIVE) {
                // Only process if the record matches the currently selected section
                if (recordData.section === selectedSection) {
                    // Check if this is a new record (either no activeRecord or different ID)
                    if (!activeRecord || recordData._id !== activeRecord._id) {
                        setActiveRecord(recordData);
                        setIsProcessingRecord(false);
                    }
                }
            }
            // Handle updateRecord response for existing record
            else if (recordData._id && activeRecord && recordData._id === activeRecord._id) {
                // Only update if the record matches the currently selected section
                if (recordData.section === selectedSection) {
                    // Update the local activeRecord with the updated data
                    setActiveRecord(recordData);
                    setIsProcessingRecord(false);
                }
            }
        }
    }, [recordData, activeRecord, hasCheckedActiveRecord, selectedSection]);

    const handleStartNewExamination = async (mode = sessionModeType.UNLIMITED) => {
        if (hasReachedLimit) {
            setShowDemoLimitModal(true);
            return;
        }

        if (recordError) {
            console.error('Cannot start examination due to record error:', recordError);
            return;
        }

        if (!userData?.id) {
            console.error('Cannot start examination: User not authenticated');
            return;
        }

        try {
            if (userData?.isDemo) {
                const result = await consumeTrial();
                if (!result?.allowed) {
                    setShowDemoLimitModal(true);
                    return;
                }
            }
        } catch (err) {
            if (err?.response?.status === 403) {
                setShowDemoLimitModal(true);
                return;
            }

            console.error('Unable to update demo trial usage:', err);
            return;
        }

        setPracticeMode(mode);
        setIsExaminationStarted(true);
        dispatch(createRecordAndGetFirstQuestion.request({
            recordData: {
                userId: userData.id,
                testType: 'SAT Practice',
                section: selectedSection,
                sessionMode_type: mode
            },
            questionRequest: {
                section: selectedSection,
                userId: userData.id
            }
        }));
    };

    const handleContinueExamination = () => {
        const pendingQuestionKey = activeRecord?._id
            ? `practice_pending_question_${activeRecord._id}`
            : null;
        const pendingQuestionIdFromSession = pendingQuestionKey
            ? sessionStorage.getItem(pendingQuestionKey)
            : null;

        setPracticeMode(activeRecord?.sessionMode_type || sessionModeType.UNLIMITED);
        setIsExaminationStarted(true);
        setShowContinueModal(false);
        setHasCheckedActiveRecord(true);
        dispatch(resumeRecordAndGetQuestion.request({
            recordId: activeRecord._id,
            questionRequest: {
                section: selectedSection,
                userId: userData.id,
                resumeFromIndex: activeRecord.currentQuestionIndex,
                currentQuestionId: activeRecord.currentQuestionId || pendingQuestionIdFromSession
            }
        }));
    };

    const handleStartFresh = () => {
        setShowContinueModal(false);
        setActiveRecord(null);
        setHasCheckedActiveRecord(true);
        handleStartNewExamination(activeRecord?.sessionMode_type || sessionModeType.UNLIMITED);
    };

    const handleGoBack = () => {
        const targetPath = '/user-sat-practice';
        navigate(targetPath);
    };

    return (
        <>
            <Helmet>
                <title>User Practice | Prepal</title>
                <meta name="description" content="User Practice for Prepal account holders." />
                <meta name="keywords" content="Prepal, user practice, education" />
                <meta name="author" content="Prepal Team" />
            </Helmet>
            <div className={styles.userPracticeScreen}>
                {/* Demo limit reached modal */}
                {showDemoLimitModal && (
                    <div className={styles.demoLimitOverlay}>
                        <div className={styles.demoLimitModal}>
                            <div className={styles.demoLimitIcon}>🔒</div>
                            <h3 className={styles.demoLimitTitle}>Demo Limit Reached</h3>
                            <p className={styles.demoLimitDesc}>
                                You've used all <strong>{DEMO_PRACTICE_LIMIT}</strong> demo practice sessions available in demo mode.
                                Please contact Prepal to get full access.
                            </p>
                        </div>
                    </div>
                )}

                {/* Modal for continuing existing session */}
                <ContinueSessionModal
                    isVisible={showContinueModal}
                    selectedSection={selectedSection}
                    onContinue={handleContinueExamination}
                    onStartFresh={handleStartFresh}
                />

                {!isExaminationStarted ? (
                    recordError ? (
                        <div className={styles.errorContainer}>
                            <h2>Session Error</h2>
                            <p>{recordError}</p>
                            <p>Please try again or go back to the practice selection page.</p>
                            <div className={styles.errorButtons}>
                                <button onClick={() => {
                                    dispatch(clearRecordError());
                                    if (selectedSection && userData?.id) {
                                        dispatch(checkActiveRecord.request({
                                            userId: userData.id,
                                            section: selectedSection
                                        }));
                                    }
                                }}>Retry</button>
                                <button onClick={handleGoBack}>Go Back</button>
                            </div>
                        </div>
                    ) : (recordLoading && isProcessingRecord && !hasCheckedActiveRecord) ? (
                        <LoadingSpinner message="Checking for active sessions..." />
                    ) : (
                        <PracticeReadyScreen
                            onStartExamination={handleStartNewExamination}
                            onGoBack={handleGoBack}
                            disabled={!!recordError || recordLoading}
                            topicName={selectedSection}
                        />
                    )
                ) : isLoading || recordLoading ? (
                    <LoadingSpinner message="Loading questions..." />
                ) : error ? (
                    <div className={styles.errorContainer}>
                        <h2>Error Loading Questions</h2>
                        <p>{error}</p>
                        <button onClick={() => handleStartNewExamination()}>Retry</button>
                    </div>
                ) : (
                    <>
                        <ExaminationHeader
                            isExamination={false}
                            onPracticeMenuClick={handlePracticeMenuClick}
                        />
                        {practiceMode === sessionModeType.SECTION_25 ? (
                            <Section25ExaminationContent
                                question={questionData.question}
                                section={selectedSection}
                                onEndSession={handleEndAndLeave}
                            />
                        ) : (
                            <ExaminationContent
                                question={questionData.question}
                                section={selectedSection}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Navigation confirmation modal */}
            {practiceMode === sessionModeType.UNLIMITED && (
                <NavigationConfirmModal
                    isOpen={showNavigationModal}
                    onPauseAndLeave={handlePauseAndLeave}
                    onEndAndLeave={handleEndAndLeave}
                    onContinueSession={handleContinueSession}
                />
            )}
            {practiceMode === sessionModeType.SECTION_25 && (
                <NavigationConfirmModal
                    isOpen={showNavigationModal}
                    onPauseAndLeave={null}
                    onEndAndLeave={handleEndAndLeave}
                    onContinueSession={handleContinueSession}
                />
            )}
        </>
    );
};

export default UserPracticeScreen;
